import { useRef } from "react";
import { toast } from "sonner";
import { Camera, Download, FileText, Paperclip, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useFetch } from "@/hooks/useFetch";
import { api, downloadFile, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

export const QC_KINDS = [
  { kind: "QC Document", label: "Add QC documents", icon: Paperclip, accept: ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png", testid: "qc-add-docs" },
  { kind: "QC Photo", label: "Add pictures", icon: Camera, accept: "image/jpeg,image/png,image/webp,.heic", testid: "qc-add-photos" },
];

export async function uploadQcFiles(sid, picked, onKindDone) {
  for (const { kind } of QC_KINDS) {
    const files = picked.filter((p) => p.kind === kind);
    for (let i = 0; i < files.length; i += 20) {
      const fd = new FormData();
      fd.append("kind", kind);
      files.slice(i, i + 20).forEach((p) => fd.append("files", p.file));
      await api.post(`/qc-submissions/${sid}/files`, fd);
    }
    if (files.length) onKindDone(kind);
  }
}

function PickButton({ k, onAdd }) {
  const ref = useRef(null);
  const Icon = k.icon;
  return (
    <>
      <input ref={ref} type="file" multiple accept={k.accept} className="hidden" data-testid={`${k.testid}-input`}
        onChange={(e) => { onAdd([...e.target.files].map((file) => ({ kind: k.kind, file }))); e.target.value = ""; }} />
      <Button type="button" variant="outline" size="sm" onClick={() => ref.current?.click()} data-testid={k.testid}><Icon className="h-4 w-4 mr-1" />{k.label}</Button>
    </>
  );
}

export function QcFilePicker({ picked, setPicked }) {
  const add = (list) => {
    const big = list.filter((p) => p.file.size > 25 * 1024 * 1024);
    if (big.length) toast.error(`Over 25 MB, skipped: ${big.map((p) => p.file.name).join(", ")}`);
    setPicked((cur) => [...cur, ...list.filter((p) => p.file.size <= 25 * 1024 * 1024)]);
  };
  return (
    <div className="space-y-2" data-testid="qc-file-picker">
      <div className="flex flex-wrap gap-2">{QC_KINDS.map((k) => <PickButton key={k.kind} k={k} onAdd={add} />)}</div>
      {picked.length > 0 && (
        <ul className="text-xs space-y-1 max-h-36 overflow-y-auto" data-testid="qc-picked-list">
          {picked.map((p, i) => (
            <li key={`${p.file.name}-${i}`} className="flex items-center gap-2 rounded bg-slate-50 px-2 py-1">
              {p.kind === "QC Photo" ? <Camera className="h-3.5 w-3.5 text-slate-500" /> : <FileText className="h-3.5 w-3.5 text-slate-500" />}
              <span className="truncate flex-1">{p.file.name}</span>
              <button type="button" onClick={() => setPicked((cur) => cur.filter((_, j) => j !== i))} data-testid={`qc-picked-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function QcFileList({ sid, testid = "qc-file-list" }) {
  const { data } = useFetch(`/qc-submissions/${sid}/files`);
  if (!data?.length) return <p className="text-xs text-slate-500" data-testid={`${testid}-empty`}>No QC documents or pictures yet</p>;
  return (
    <ul className="text-xs space-y-1" data-testid={testid}>
      {data.map((d) => (
        <li key={d.id} className="flex items-center gap-2 rounded border border-slate-200 px-2 py-1">
          {d.doc_type === "QC Photo" ? <Camera className="h-3.5 w-3.5 text-slate-500" /> : <FileText className="h-3.5 w-3.5 text-slate-500" />}
          <span className="truncate flex-1" title={d.stored_name}>{d.original_filename}</span>
          <span className="rounded bg-slate-100 px-1.5 text-[10px] text-slate-600">{d.doc_type === "QC Photo" ? "Picture" : "Document"} v{d.version}</span>
          <span className="text-slate-400 hidden sm:inline">{d.uploaded_by?.name} · {fmtDateTime(d.uploaded_at)}</span>
          <Button variant="ghost" size="icon" className="h-6 w-6" data-testid={`qc-file-download-${d.id}`}
            onClick={() => downloadFile(`/qc-submissions/${sid}/files/${d.id}/download`, d.stored_name).catch((e) => toast.error(errMsg(e)))}><Download className="h-3.5 w-3.5" /></Button>
        </li>
      ))}
    </ul>
  );
}

export function QcFilesDialog({ s, onClose }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg w-[95vw]" data-testid="qc-files-dialog">
        <DialogHeader><DialogTitle>QC documents & pictures · {s.lm_number}</DialogTitle></DialogHeader>
        <QcFileList sid={s.id} />
      </DialogContent>
    </Dialog>
  );
}
